import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { MerchantTypeService } from 'src/app/services/merchant-type.service';
import { AuthService } from 'src/app/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { LocationService } from 'src/app/services/location.service';
import type {
  EnrichedLocationData,
  LocationScores,
  Demographics,
  CompetitionData,
} from 'src/app/models/location.models';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';
import { OnboardingService } from 'src/app/services/onboarding.service';
import {
  MerchantType,
  FixedMerchantData,
  ItinerantMerchantData,
  EstimatedEconomies,
  EventType,
  Affluence,
  Coverage,
  TeamSize,
  Periodicity,
  ServiceTime,
  CompleteOnboardingPayload,
} from 'src/app/models/onboarding.model';
import type { ActivityType, Positioning } from 'src/app/models/cluster.model';
import { ClusterService } from 'src/app/services/cluster.service';

import {
  SERVICE_LABELS,
  COVERAGE_OPTIONS,
  PERIODICITY_LABELS,
  ACTIVITY_TYPE_LABELS,
  EVENT_TYPE_LABELS,
  ACTIVITY_TYPE_CONFIG,
  POSITIONING_LABELS,
} from 'src/app/models/onboarding.labels';
import { LogService } from '../../../services/log.service';

@Component({
  selector: 'app-resumeEtPotentiel',
  imports: [CommonModule, ReactiveFormsModule, ProgressBarComponent],
  templateUrl: './resumeEtPotentiel.component.html',
  styleUrls: ['./resumeEtPotentiel.component.css'],
})
export class ResumeEtPotentielComponent implements OnInit {
  private log = inject(LogService);
  isLoading = true;
  isNavigating = false;

  currentStep = 6;
  totalSteps = 6;

  // ===== Données affichées =====
  merchantType!: MerchantType;
  /** @deprecated — utiliser activityTypeLabel */
  activityTypeFallbackLabel = '';

  coverageLabel = '';
  coverageDescription = '';
  teamSizeLabel = '';

  // FIXE
  servicesLabel = '';
  periodiciteLabel = '';
  affluenceLabel = '';

  // ITINÉRANT
  eventTypesLabel = '';

  // ÉCONOMIES estimées
  estimatedEconomies: EstimatedEconomies = {
    wasteReduction: 0,
    avoidedRuptures: 0,
    timeSaved: 0,
    total: 0,
    roi: 0,
  };

  // Infos activité et positionnement
  activityTypeLabel = '';
  positioningLabel = '';
  activityType: ActivityType | null = null;
  positioning: Positioning | null = null;

  // Enrichissement géo
  enrichmentLoading = true;
  enrichmentAvailable = false;
  enrichedData: EnrichedLocationData | null = null;
  scores: LocationScores | null = null;
  demographics: Demographics | null = null;
  competition: CompetitionData | null = null;
  enrichmentAddress = '';
  enrichmentCity = '';
  zoneEconomique = '';
  fluxClient = '';

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  serviceLabels = SERVICE_LABELS;
  coverageOptions = COVERAGE_OPTIONS;
  periodicityLabels = PERIODICITY_LABELS;
  activityTypeLabels = ACTIVITY_TYPE_LABELS;
  eventTypeLabels = EVENT_TYPE_LABELS;

  constructor(
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private merchantTypeService: MerchantTypeService,
    private clusterService: ClusterService,
    private locationService: LocationService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // Fonction pour retirer les emojis des labels
  private removeEmoji(text: string): string {
    // Supprime les emojis et nettoie les espaces
    return text
      .replace(
        /[\u{1F000}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
        '',
      )
      .trim();
  }

  private loadData(): void {
    this.isLoading = true;

    this.onboardingStateService.setCurrentStep(6);

    // Identifier le type de marchand : locationType
    const data = this.onboardingStateService.getFormData();

    if (!data?.merchantType || !data.activityType) {
      this.log.error('❌ Données onboarding invalides');
      this.isLoading = false;
      return;
    }

    this.merchantType = data.merchantType;

    // Afficher activityType et positioning
    this.activityType = data.activityType || null;
    this.positioning = data.positioning || null;
    if (this.activityType && ACTIVITY_TYPE_CONFIG[this.activityType]) {
      this.activityTypeLabel = ACTIVITY_TYPE_CONFIG[this.activityType].label;
    }
    this.activityTypeFallbackLabel =
      this.activityTypeLabel ||
      this.removeEmoji(
        this.activityTypeLabels[data.activityType] ?? data.activityType,
      );
    if (this.positioning && POSITIONING_LABELS[this.positioning]) {
      this.positioningLabel = POSITIONING_LABELS[this.positioning].label;
    }

    // Adresse saisie à l'étape 3 — disponible même sans enrichissement
    const savedAddress = data.address;
    if (savedAddress) {
      this.enrichmentAddress =
        savedAddress.formatted || savedAddress.address || '';
      this.enrichmentCity = savedAddress.city || '';
    }

    this.log.debug('Données récupérées:', data);

    if (this.merchantType === MerchantType.FIXED) {
      this.affluenceLabel = this.formatAffluence(data.affluence);
      this.coverageLabel = this.formatCoverage(data.coverage);
      this.teamSizeLabel = this.formatTeamSize(data.teamSize);
      this.servicesLabel = this.removeEmoji(this.formatServices(data.services));
      this.periodiciteLabel = this.formatPeriodicite(data.periodicity);

      this.calculateEconomiesFixed(data);
    }

    if (this.merchantType === MerchantType.ITINERANT) {
      this.eventTypesLabel = this.formatEventTypes(data.eventTypes);
      this.coverageLabel = this.formatCoverage(data.coverage);
      this.teamSizeLabel = this.formatTeamSize(data.teamSize);

      this.calculateEconomiesItinerant(data);
    }

    this.isLoading = false;

    // Charger les données d'enrichissement géo
    this.loadEnrichmentData();
  }

  private loadEnrichmentData(): void {
    this.enrichmentLoading = true;
    const user = this.authService.getCurrentUser();
    const userId = (user as any)?.id || (user as any)?._id;

    if (!userId) {
      this.log.warn("⚠️ Pas de userId pour charger l'enrichissement");
      this.enrichmentLoading = false;
      return;
    }

    this.locationService
      .getLocationProfile(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          if (profile?.hasLocation && profile.enrichedData) {
            this.enrichmentAvailable = true;
            this.enrichedData = profile.enrichedData;
            this.scores = profile.enrichedData.scores || null;
            this.demographics = profile.enrichedData.demographics || null;
            this.competition = profile.enrichedData.similarLocations || null;
            this.enrichmentAddress = profile.address || '';
            this.enrichmentCity = profile.city || '';

            // Déduction zone/flux (stocké dans scores.insights ou via clusterDeduction)
            const enrichedAny = profile.enrichedData as any;
            if (enrichedAny?.clusterDeduction) {
              this.zoneEconomique = this.formatZoneLabel(
                enrichedAny.clusterDeduction.economicZone,
              );
              this.fluxClient = this.formatFlowLabel(
                enrichedAny.clusterDeduction.clientFlow,
              );
            }

            this.log.debug(
              '✅ Données enrichissement chargées:',
              this.enrichedData,
            );
          } else {
            this.enrichmentAvailable = false;
            this.log.debug('ℹ️ Pas de données enrichissement disponibles');
          }
          this.enrichmentLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.warn('⚠️ Erreur chargement enrichissement:', err);
          this.enrichmentAvailable = false;
          this.enrichmentLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private formatZoneLabel(zone: string): string {
    const labels: Record<string, string> = {
      metro_core: '🏙️ Centre métropole',
      metro_suburb: '🏘️ Banlieue métropole',
      mid_city: '🏢 Ville moyenne',
      small_town_rural: '🌿 Petite ville / Rural',
      tourist_zone: '🏖️ Zone touristique',
    };
    return labels[zone] || zone || 'Non déterminée';
  }

  private formatFlowLabel(flow: string): string {
    const labels: Record<string, string> = {
      regular_local: '👥 Clientèle locale régulière',
      pedestrian_traffic: '🚶 Flux piéton',
      captive_transit: '🚇 Transit captif',
      weekly_market: '🛒 Marché hebdomadaire',
      event_festival: '🎪 Événements / Festivals',
      corporate_private: '🏢 Corporate / Privé',
    };
    return labels[flow] || flow || 'Non déterminé';
  }

  private calculateEconomiesFixed(data: any): void {
    const merchantData: FixedMerchantData = {
      merchantType: MerchantType.FIXED,
      activityType: data.activityType,
      affluence: data.affluence,
      coverage: data.coverage,
      teamSize: data.teamSize,
      services: data.services,
      periodicity: data.periodicity,
    };

    // 1. Récupérer le cluster depuis le backend
    this.merchantTypeService.getCluster(merchantData).subscribe({
      next: (cluster) => {
        // 2. Calculer les économies avec cluster récupéré
        this.estimatedEconomies = this.merchantTypeService.estimateEconomies(
          merchantData,
          cluster,
        );
        this.log.debug('Économies FIXE calculées:', this.estimatedEconomies);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.warn(
          '⚠️ Cluster FIXE indisponible, utilisation des valeurs par défaut',
        );
        this.setDefaultEconomies();
        this.cdr.markForCheck();
      },
    });
  }

  private calculateEconomiesItinerant(data: any): void {
    const merchantData: ItinerantMerchantData = {
      merchantType: MerchantType.ITINERANT,
      activityType: data.activityType,
      eventTypes: data.eventTypes ?? [],
      coverage: data.coverage,
      teamSize: data.teamSize,
    };

    // 1. Récupérer le cluster depuis le backend
    this.merchantTypeService.getClusterItinerant(merchantData).subscribe({
      next: (cluster) => {
        // 2. Calculer les économies avec cluster récupéré
        this.estimatedEconomies =
          this.merchantTypeService.estimateEconomiesItinerant(
            merchantData,
            cluster,
          );
        this.log.debug('Économies ITINÉRANT calculées:', this.estimatedEconomies);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.warn(
          '⚠️ Cluster ITINÉRANT indisponible, utilisation des valeurs par défaut',
        );
        this.setDefaultEconomies();
        this.cdr.markForCheck();
      },
    });
  }

  private setDefaultEconomies(): void {
    this.estimatedEconomies = {
      wasteReduction: 150,
      avoidedRuptures: 100,
      timeSaved: 80,
      total: 250,
      roi: 400,
    };
  }

  // ===============================
  // Formatters UI
  // ===============================
  private formatAffluence(value?: Affluence): string {
    if (!value) return 'Non renseigné';
    return { low: 'Faible', medium: 'Moyen', high: 'Élevé' }[value];
  }

  private formatPeriodicite(value?: Periodicity): string {
    if (!value) return 'Non renseigné';
    return this.periodicityLabels[value];
  }

  private formatCoverage(value?: Coverage): string {
    if (!value) return 'Non renseigné';
    const option = this.coverageOptions.find((o) => o.id === value);
    if (!option) return 'Non renseigné';
    this.coverageDescription = option.description; // <-- nouvelle propriété
    return this.removeEmoji(option.label);
  }

  private formatTeamSize(value?: TeamSize): string {
    if (!value) return 'Non renseigné';
    return {
      solo: 'Solo',
      '2persons': '2 personnes',
      '3plus': '3+ personnes',
      multi_stand: 'Plusieurs points de ventes',
    }[value];
  }

  private formatServices(services?: ServiceTime[]): string {
    if (!services?.length) return 'Aucun service sélectionné';
    return services.map((s) => this.serviceLabels[s]).join(' · ');
  }

  private formatEventTypes(types?: EventType[]): string {
    if (!types?.length) return 'Aucun événement sélectionné';
    return Array.from(
      new Set(types.map((t) => this.removeEmoji(this.eventTypeLabels[t]))),
    ).join(', ');
  }

  // ===============================
  // Navigation
  // ===============================
  finalize(): void {
    if (this.isNavigating) return;

    this.isNavigating = true;
    this.log.debug('✅ Finalisation de l’onboarding');

    const data = this.onboardingStateService.getFormData();
    this.log.debug('📦 Données du state:', data);

    const payload: CompleteOnboardingPayload = {
      merchantType: data.merchantType,
      activityType: data.activityType,
      affluence: data.affluence,
      coverage: data.coverage,
      eventTypes: data.eventTypes,
      periodicity: data.periodicity,
      services: data.services,
      teamSize: data.teamSize,
      positioning: data.positioning,
    };

    this.log.debug('📤 Payload envoyé au backend:', payload);

    this.onboardingService.completeOnboarding(payload).subscribe({
      next: async (response) => {
        if (response?.token) {
          this.authService.setToken(response.token);
          // Note: setToken() met déjà à jour le user dans sessionStorage

          // ⏳ Attendre que l'utilisateur soit bien rechargé
          await this.authService.refreshCurrentUser();
        }

        // Créer le ClusterMarchand si activityType est défini
        if (data.activityType) {
          this.clusterService
            .createMerchantCluster({
              activityType: data.activityType,
              positioning: data.positioning || 'standard',
            })
            .subscribe({
              next: (cluster) =>
                this.log.debug('✅ ClusterMarchand créé:', cluster),
              error: (err) =>
                this.log.warn(
                  '⚠️ Erreur création ClusterMarchand (non bloquant):',
                  err,
                ),
            });
        }

        // ✅ Navigation uniquement quand tout est prêt
        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.log.error('❌ Erreur completion onboarding', err);
        this.isNavigating = false;
        this.cdr.markForCheck();
      },
    });
  }

  goBack(): void {
    // Retour vers l'étape produits (step 5)
    this.router.navigate(['/onboarding/step/5']);
  }

  formatCurrency(value: number): string {
    return `${Math.round(value).toLocaleString('fr-FR')}€`;
  }

  formatCurrency2(value: number): string {
    return `${Math.round(value).toLocaleString('fr-FR')} Euros`;
  }

  formatTimeSaved(value: number): string {
    return `${Math.round(value / 60)} heures`;
  }

  formatROI(value: number): string {
    return `${Math.round(value)}%`;
  }
}
